/**
 * SectionHelpIcon — Inline (?) icon that shows a tooltip/popover
 * with a 2–3 sentence plain-English explanation of a feature or section.
 *
 * Zero-delay tooltip. No tab-switching needed to find help.
 */
import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import { clsx } from 'clsx';

interface SectionHelpIconProps {
    content: string;
    className?: string;
    /** Size of the icon in pixels. Default: 14 */
    size?: number;
}

export const SectionHelpIcon: React.FC<SectionHelpIconProps> = ({ content, className, size = 14 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    return (
        <div ref={ref} className={clsx("relative inline-flex", className)}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-muted-foreground/60 hover:text-muted-foreground transition-colors focus:outline-none"
                title="What is this?"
                type="button"
            >
                <HelpCircle style={{ width: size, height: size }} />
            </button>

            {isOpen && (
                <div className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-1.5 w-64 max-w-[calc(100vw-2rem)]">
                    <div className="bg-popover text-popover-foreground text-xs leading-relaxed p-3 rounded-lg shadow-lg border border-border">
                        {content}
                    </div>
                </div>
            )}
        </div>
    );
};
